import { orpc } from '@/api/rpc/client';
import {
  AUTHZ_CACHE_TIME_MS,
  getAuthzQueryKey,
} from '@/context/authz/AuthzClient';
import { Action, Subject } from '@erp/authz';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';

/**
 * Hook to check if the current user can perform an action on a subject
 *
 * Authorization results are cached for 30 minutes (via AUTHZ_CACHE_TIME_MS) to reduce
 * backend load. Permissions are kept fresh via:
 * - Dynamic fetches overwriting initial entries for base abilities
 * - useCurrentUser updates repopulating abilities when user changes
 *
 * Returns a tuple: [allowed, queryState]
 * - allowed: boolean indicating if the action is allowed (false while loading)
 * - queryState: object containing isLoading, isError, error for advanced usage
 *
 * @param action - The action to perform
 * @param subject - The subject/resource type
 *
 * @example
 * // Simple usage - just check permission
 * const [canApprove] = useCan('approve', 'APInvoice');
 *
 * @example
 * // Advanced usage - handle loading/error states
 * const [canApprove, { isLoading, isError }] = useCan('approve', 'APInvoice');
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 */
export function useCan(
  action: Action,
  subject: Subject,
): [boolean, { isLoading: boolean; isError: boolean; error: Error | null }] {
  const { data, isLoading, isError, error } = useQuery(
    orpc.authz.can.queryOptions({
      queryKey: getAuthzQueryKey({ action, subject }),
      staleTime: AUTHZ_CACHE_TIME_MS,
      gcTime: Infinity, // Keep in cache indefinitely (no garbage collection)
      input: { action, subject },
    }),
  );

  return [
    Boolean(data?.allowed),
    { isLoading, isError, error: error as Error | null },
  ];
}

/**
 * Can Component
 *
 * Conditional rendering based on permissions with loading and fallback support
 *
 * @param I - Action to check
 * @param a - Subject to check
 * @param loading - Optional loading fallback (default: hide content while loading)
 * @param noAccessFallback - Optional no-access fallback (default: hide content when denied)
 * @param children - Content to show when permission is granted
 *
 * @example
 * ```tsx
 * // Simple usage - hide while loading and if denied
 * <Can I="approve" a="APInvoice">
 *   <ApproveButton />
 * </Can>
 * ```
 *
 * @example
 * ```tsx
 * // With loading state
 * <Can I="approve" a="APInvoice" loading={<Spinner />}>
 *   <ApproveButton />
 * </Can>
 * ```
 *
 * @example
 * ```tsx
 * // With no-access fallback
 * <Can
 *   I="approve"
 *   a="APInvoice"
 *   noAccessFallback={<LockedIcon />}
 * >
 *   <ApproveButton />
 * </Can>
 * ```
 *
 * @example
 * ```tsx
 * // With both loading and no-access fallback
 * <Can
 *   I="approve"
 *   a="APInvoice"
 *   loading={<Spinner />}
 *   noAccessFallback={<div>You cannot approve this</div>}
 * >
 *   <ApproveButton />
 * </Can>
 * ```
 */
export function Can({
  I: action,
  a: subject,
  loading,
  noAccessFallback,
  children,
}: {
  I: Action;
  a: Subject;
  loading?: ReactNode;
  noAccessFallback?: ReactNode;
  children: ReactNode;
}) {
  const [allowed, { isLoading }] = useCan(action, subject);

  if (isLoading) {
    return loading !== undefined ? <>{loading}</> : null;
  }

  if (!allowed) {
    return noAccessFallback !== undefined ? <>{noAccessFallback}</> : null;
  }

  return <>{children}</>;
}
